import {
  Children,
  type CSSProperties,
  type ReactElement,
  type ReactNode,
  type Ref,
  cloneElement,
  forwardRef,
  isValidElement,
  useCallback,
} from 'react';
import { cn } from '../lib/utils.js';

export type SlotProps = React.HTMLAttributes<HTMLElement> & {
  /** Exactly one React element. It is rendered in the Slot's place, carrying the Slot's props. */
  children?: ReactNode;
};

/**
 * Renders its single child element in its own place, with the Slot's props merged onto it. This
 * is what powers `asChild`: `<Button asChild><a href="/x" /></Button>` gives the anchor the
 * button's classes instead of nesting an anchor inside a `<button>`.
 *
 * How the two sets of props combine:
 * - event handlers (`on[A-Z]…`) present on both are composed — the child's runs first, then the
 *   Slot's, and the child's return value is kept;
 * - `className` values are merged with `cn()`, the child's classes winning a Tailwind conflict;
 * - `style` objects are shallow-merged, the child's keys winning;
 * - refs on both are composed, so both receive the DOM node;
 * - every other prop takes the child's value when the child sets it, the Slot's otherwise.
 *
 * A child that is not a React element (text, `null`, `false`) renders nothing; more than one
 * child throws, because there would be no single element to hand the props to.
 */
export const Slot = forwardRef<HTMLElement, SlotProps>(function Slot(
  { children, ...slotProps },
  forwardedRef,
) {
  const child = isValidElement(children) ? children : null;
  const childRef = child ? getElementRef(child) : undefined;
  const composedRef = useCallback(
    (node: HTMLElement | null) => {
      setRef(forwardedRef, node);
      setRef(childRef, node);
    },
    [forwardedRef, childRef],
  );

  if (!child) {
    if (Children.count(children) > 1) {
      throw new Error('Slot expects a single React element child.');
    }
    return null;
  }

  return cloneElement(child, {
    ...mergeProps(slotProps, child.props as Record<string, unknown>),
    ref: forwardedRef || childRef ? composedRef : undefined,
  } as Record<string, unknown>);
});

/** The rules documented on {@link Slot}; the result is spread over the child's own props. */
function mergeProps(
  slotProps: Record<string, unknown>,
  childProps: Record<string, unknown>,
): Record<string, unknown> {
  const merged: Record<string, unknown> = { ...slotProps };

  for (const name of Object.keys(childProps)) {
    const slotValue = slotProps[name];
    const childValue = childProps[name];

    if (/^on[A-Z]/.test(name) && typeof slotValue === 'function') {
      merged[name] =
        typeof childValue === 'function'
          ? (...args: unknown[]) => {
              const result = (childValue as (...a: unknown[]) => unknown)(...args);
              (slotValue as (...a: unknown[]) => unknown)(...args);
              return result;
            }
          : slotValue;
    } else if (name === 'className') {
      merged[name] = cn(slotValue as string | undefined, childValue as string | undefined);
    } else if (name === 'style') {
      merged[name] = { ...(slotValue as CSSProperties), ...(childValue as CSSProperties) };
    } else {
      merged[name] = childValue;
    }
  }

  return merged;
}

function setRef<T>(ref: Ref<T> | undefined, value: T | null): void {
  if (typeof ref === 'function') {
    ref(value);
  } else if (ref) {
    (ref as { current: T | null }).current = value;
  }
}

/**
 * Reads the ref an element was created with. React 18 keeps it on `element.ref` and puts a
 * warning getter on `props.ref`; React 19 does the reverse. Whichever side carries the warning
 * getter tells us to read the other one.
 */
function getElementRef(element: ReactElement): Ref<HTMLElement> | undefined {
  const props = element.props as { ref?: Ref<HTMLElement> };
  const legacy = element as unknown as { ref?: Ref<HTMLElement> };

  if (isWarningGetter(Object.getOwnPropertyDescriptor(props, 'ref')?.get)) return legacy.ref;
  if (isWarningGetter(Object.getOwnPropertyDescriptor(element, 'ref')?.get)) return props.ref;
  return props.ref ?? legacy.ref ?? undefined;
}

function isWarningGetter(getter: (() => unknown) | undefined): boolean {
  return Boolean(getter && 'isReactWarning' in getter && getter.isReactWarning);
}
